<template>
  <div class="deliverable-management">
    <!-- Breadcrumb Header (only show in standalone mode) -->
    <div v-if="!embedded" class="breadcrumb-header pa-4">
      <v-btn icon @click="closeScreen" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6">{{ localJobData.job_title || 'Novo job' }}</span>
      <v-icon class="mx-2">mdi-chevron-right</v-icon>
      <span class="text-h6 grey--text">{{ $t('deliverables_list') }}</span>

      <v-spacer></v-spacer>

      <!-- Top Action Buttons -->
      <v-btn v-if="!isCreationMode && canEditDeliverables" color="primary" class="mr-2" @click="openMultiFileUploadView">
        <v-icon left>mdi-upload-multiple</v-icon>
        {{ $t('multi_file_upload') }}
      </v-btn>

      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn outlined color="primary" class="mr-2" :disabled="effectiveReadonly" v-bind="attrs" v-on="on">
            <span>PRESETS</span>
            <v-icon right>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="openLoadBaseGroupPresetsDialog">
            <v-list-item-icon class="mr-2">
              <v-icon>mdi-download</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('load_preset') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn color="primary" @click="openAddDeliverableDialog" :disabled="!canAddDeliverable">
        <v-icon left>mdi-plus</v-icon>
        {{ $t('add_deliverable') }}
      </v-btn>
    </div>

    <v-container fluid :class="embedded ? 'pa-0' : 'pa-6'">
      <!-- Main Card -->
      <v-card outlined class="deliverables-card">
        <!-- Header Section -->
        <div class="card-header pa-4 d-flex align-center" :class="{ 'card-header--embedded': embedded }">
          <v-divider
            class="custom-vertical-divider ml-1 mr-2"
            vertical
          ></v-divider>

          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-medium">
              <v-icon class="mr-3">mdi-package-variant-closed</v-icon>{{ $t('deliverables_list') }}
            </div>
            <div class="text-caption grey--text">
              {{ baseInstances.length }} {{ $t('deliverable_singular') }} ·
              {{ totalFormats }} {{ $t('formats') }}
              <template v-if="showPriceFields">
                · <span class="primary--text font-weight-medium">{{ formatCurrency(totalValue) }}</span>
              </template>
            </div>
          </div>

          <v-btn text class="mr-2 deliverable-toggle-all" @click="toggleAll" small>
            <v-icon left>{{ allDeliverablesExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
            {{ allDeliverablesExpanded ? $t('close_all') : $t('open_all') }}
          </v-btn>

          <v-btn-toggle mandatory dense value="deliverables" class="ml-2 deliverable-nav-toggle">
            <v-btn value="assets-list" icon :title="$t('deliverables_list')" :aria-label="$t('deliverables_list')" @click="openDeliverableAssetsListView">
              <v-icon>mdi-format-list-bulleted</v-icon>
            </v-btn>
            <v-btn value="deliverables" color="primary" icon :title="$t('deliverables_list')" :aria-label="$t('deliverables_list')">
              <v-icon color="primary">mdi-format-list-text</v-icon>
            </v-btn>
            <v-btn value="assets" icon :title="$t('deliverable_assets')" :aria-label="$t('deliverable_assets')" @click="openDeliverableAssetsView">
              <v-icon>mdi-list-status</v-icon>
            </v-btn>
          </v-btn-toggle>

          <!-- The drawer keeps the same navigation, using only the compact icon group above. -->
          <template v-if="embedded">
            <v-btn v-if="!isCreationMode && canEditDeliverables" outlined color="primary" class="embedded-header-action" :title="$t('multi_file_upload')" :aria-label="$t('multi_file_upload')" @click="openMultiFileUploadView">
              <v-icon small>mdi-tray-arrow-up</v-icon>
              <span class="embedded-header-action-label">{{ $t('multi_file_upload') }}</span>
            </v-btn>
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined color="primary" class="embedded-header-action" :disabled="effectiveReadonly" title="PRESETS" aria-label="PRESETS" v-bind="attrs" v-on="on">
                  <v-icon small>mdi-bookmark-outline</v-icon>
                  <span class="embedded-header-action-label">PRESETS</span>
                  <v-icon class="embedded-header-action-label" right>mdi-menu-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="openLoadBaseGroupPresetsDialog">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-download</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('load_preset') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn color="primary" class="embedded-header-action" :title="$t('add_deliverable')" :aria-label="$t('add_deliverable')" @click="openAddDeliverableDialog" :disabled="!canAddDeliverable">
              <v-icon small>mdi-plus</v-icon>
              <span class="embedded-header-action-label">{{ $t('add_deliverable') }}</span>
            </v-btn>
          </template>
        </div>

        <!-- Deliverables List -->
        <div class="pa-4">
          <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

          <div v-if="baseInstances.length === 0 && !loading" class="text-center py-12 grey--text">
            <v-icon large color="grey lighten-1">mdi-package-variant-closed</v-icon>
            <div class="mt-2">{{ $t('no_deliverables_added') }}</div>
            <v-btn color="primary" class="mt-4" @click="openAddDeliverableDialog" :disabled="!canAddDeliverable">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('add_deliverable') }}
            </v-btn>
          </div>

          <!-- Deliverable Items -->
          <v-form ref="deliverablesListForm">
            <v-card v-for="(baseInstance, index) in baseInstances" :key="baseInstance.base_instance_id" class="deliverable-item mb-4" outlined>
              <div class="deliverable-header" :class="{ 'expanded': expandedDeliverables[baseInstance.base_instance_id] }">
                <div class="d-flex align-center pa-3 deliverable-header-row" :class="{ 'mb-3': expandedDeliverables[baseInstance.base_instance_id] }">
                  <v-btn icon @click="toggleDeliverable(baseInstance.base_instance_id)" class="mr-2">
                    <v-icon>
                      {{ expandedDeliverables[baseInstance.base_instance_id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </v-btn>

                  <v-chip color="primary lighten-2" class="mr-3 font-weight-bold black--text" label>
                    #{{ index + 1 }}
                  </v-chip>

                  <v-icon class="mr-2">{{ getBaseInstanceIcon(baseInstance) }}</v-icon>

                  <span class="font-weight-medium mr-2">{{ baseInstance.base_title }}</span>
                  <span v-if="showPriceFields" class="primary--text font-weight-medium">· {{ formatCurrency(getBaseInstanceTotal(baseInstance)) }}</span>

                  <v-spacer></v-spacer>

                  <v-chip outlined class="mr-2">
                    {{ getStatusTitle(baseInstance.status_id) }}
                  </v-chip>
                  <v-chip outlined class="mr-2">
                    <v-icon left small>mdi-image-multiple-outline</v-icon>
                    {{ baseInstance.formatInstances?.length || 0 }} {{ $t('formats') }}
                  </v-chip>
                  <v-chip outlined class="mr-2">
                    <v-icon left small>mdi-file-image-outline</v-icon>
                    {{ getBaseInstanceAssetCount(baseInstance) }} {{ $t('assets') }}
                  </v-chip>

                  <v-menu v-if="!effectiveReadonly" offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-dots-horizontal</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="openDuplicateBaseInstanceDialog(baseInstance)">
                        <v-list-item-icon class="mr-2">
                          <v-icon>mdi-content-duplicate</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('duplicate') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isCreationMode" @click="openSaveFormatsAsPresetDialog(baseInstance)">
                        <v-list-item-icon class="mr-2">
                          <v-icon>mdi-content-save</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('save_formats_as_preset') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="deleteBaseInstance(baseInstance.base_instance_id)">
                        <v-list-item-icon class="mr-2">
                          <v-icon>mdi-delete</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <!-- Expanded Content -->
                <v-expand-transition>
                  <div v-show="expandedDeliverables[baseInstance.base_instance_id]" class="deliverable-content pa-4 pt-0">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="baseInstance.base_title"
                          :label="$t('title')"
                          outlined
                          dense
                          :readonly="effectiveReadonly"
                          :rules="effectiveReadonly ? [] : [rules.requiredText]"
                          background-color="white"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-autocomplete
                          v-model="baseInstance.deliverable_id"
                          :items="deliverableOptions"
                          :label="$t('customer_item')"
                          item-text="deliverable_title"
                          item-value="deliverable_id"
                          outlined
                          dense
                          :readonly="effectiveReadonly"
                          :rules="effectiveReadonly ? [] : [rules.required]"
                          background-color="white"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-autocomplete
                          v-model="baseInstance.rate_card_id"
                          :items="rateCardOptions"
                          :label="$t('rate_card_item')"
                          item-text="rate_card_title"
                          item-value="rate_card_id"
                          outlined
                          dense
                          :readonly="effectiveReadonly"
                          hide-details
                          clearable
                          background-color="white"
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-autocomplete
                          v-model="baseInstance.status_id"
                          :items="statusOptions"
                          :label="$t('status')"
                          item-text="status_title"
                          item-value="status_id"
                          outlined
                          dense
                          :readonly="effectiveReadonly"
                          :rules="effectiveReadonly ? [] : [rules.required]"
                          background-color="white"
                        ></v-autocomplete>
                      </v-col>
                    </v-row>

                    <v-row class="mt-0">
                      <v-col cols="12" class="pt-0">
                        <MarkdownEditor
                          v-model="baseInstance.base_spec"
                          :label="$t('instructions')"
                          :placeholder="$t('enter_instructions_markdown')"
                          :rows="3"
                          hide-details
                        />
                      </v-col>
                    </v-row>

                    <!-- Formats Header -->
                    <div class="formats-header d-flex align-center mt-6 mb-3">
                      <v-icon class="mr-2">mdi-image-multiple-outline</v-icon>
                      <span class="text-subtitle-2 font-weight-medium">{{ $t('formats') }}</span>

                      <v-spacer></v-spacer>

                      <v-btn text class="mr-2" @click="toggleAllFormats(baseInstance)" small>
                        <v-icon left>{{ areAllFormatsExpanded(baseInstance) ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
                        {{ areAllFormatsExpanded(baseInstance) ? $t('close_all') : $t('open_all') }}
                      </v-btn>
                      <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn outlined color="primary" class="mr-2" :disabled="effectiveReadonly" v-bind="attrs" v-on="on">
                            <span>PRESETS</span>
                            <v-icon right>mdi-menu-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense>
                          <v-list-item @click="openLoadFormatGroupPresetsDialog(baseInstance)">
                            <v-list-item-icon class="mr-2">
                              <v-icon>mdi-download</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t('load_preset') }}</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <v-btn color="primary" @click="openAddFormatInstanceDialog(baseInstance)" :disabled="effectiveReadonly">
                        <v-icon left>mdi-plus</v-icon>
                        {{ $t('formats').toUpperCase() }}
                      </v-btn>
                    </div>

                      <!-- Format Items -->
                      <v-card v-for="formatInstance in baseInstance.formatInstances" :key="formatInstance.format_instance_id" class="format-item mb-3" elevation="0">
                        <div class="format-card" :class="{ 'expanded': expandedFormats[formatInstance.format_instance_id] }">
                          <div class="format-header pa-3">
                            <v-row align="center" no-gutters>
                              <v-col cols="auto" class="mr-2">
                                <v-btn icon @click="toggleFormat(formatInstance.format_instance_id)">
                                  <v-icon>
                                    {{ expandedFormats[formatInstance.format_instance_id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                                  </v-icon>
                                </v-btn>
                              </v-col>

                              <v-col cols="auto" class="mx-2" style="min-width: 150px">
                                <v-text-field
                                  v-model="formatInstance.format_desc"
                                  :label="$t('format')"
                                  outlined
                                  dense
                                  :readonly="effectiveReadonly"
                                  :rules="effectiveReadonly ? [] : [rules.requiredText]"
                                  background-color="white"
                                ></v-text-field>
                              </v-col>

                              <v-col>
                                <v-text-field
                                  v-model="formatInstance.format_title"
                                  :label="$t('title')"
                                  outlined
                                  dense
                                  :readonly="effectiveReadonly"
                                  :rules="effectiveReadonly ? [] : [rules.requiredText]"
                                  background-color="white"
                                ></v-text-field>
                              </v-col>

                              <v-col cols="1" class="mx-2">
                                <v-text-field
                                  :value="formatInstance.quantity"
                                  :label="$t('quantity')"
                                  type="number"
                                  outlined
                                  dense
                                  :readonly="effectiveReadonly"
                                  :rules="effectiveReadonly ? [] : [rules.positiveNumber]"
                                  min="1"
                                  step="1"
                                  @input="updateFormatQuantity(formatInstance, $event)"
                                  background-color="white"
                                ></v-text-field>
                              </v-col>

                              <v-col cols="auto" class="mr-2" style="min-width: 200px">
                                <v-autocomplete
                                  :value="formatInstance.status_id"
                                  :items="statusOptions"
                                  :label="$t('status')"
                                  item-text="status_title"
                                  item-value="status_id"
                                  outlined
                                  dense
                                  :readonly="effectiveReadonly"
                                  :rules="effectiveReadonly ? [] : [rules.required]"
                                  @change="updateFormatStatus(formatInstance, $event)"
                                  background-color="white"
                                ></v-autocomplete>
                              </v-col>

                              <v-col cols="auto" v-if="!effectiveReadonly">
                                <v-menu offset-y left>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn icon v-bind="attrs" v-on="on">
                                      <v-icon>mdi-dots-horizontal</v-icon>
                                    </v-btn>
                                  </template>
                                  <v-list dense>
                                    <v-list-item @click="openDuplicateFormatInstanceDialog(formatInstance)">
                                      <v-list-item-icon class="mr-2">
                                        <v-icon>mdi-content-duplicate</v-icon>
                                      </v-list-item-icon>
                                      <v-list-item-title>{{ $t('duplicate') }}</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="deleteFormatInstance(formatInstance.format_instance_id)">
                                      <v-list-item-icon class="mr-2">
                                        <v-icon>mdi-delete</v-icon>
                                      </v-list-item-icon>
                                      <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                                    </v-list-item>
                                  </v-list>
                                </v-menu>
                              </v-col>
                            </v-row>
                          </div>

                          <!-- Billable Items Table -->
                          <v-expand-transition>
                            <div v-show="expandedFormats[formatInstance.format_instance_id]" class="format-content pa-4 pt-0">
                              <div class="billables-section">
                                <div class="d-flex align-center mb-2">
                                  <v-btn icon small class="mr-1" @click="toggleBillables(formatInstance.format_instance_id)">
                                    <v-icon>
                                      {{ isBillablesExpanded(formatInstance.format_instance_id) ? 'mdi-menu-down' : 'mdi-menu-right' }}
                                    </v-icon>
                                  </v-btn>
                                  <span class="text-caption font-weight-medium text-uppercase">
                                    {{ $t('billable_items') }} <span class="grey--text">({{ formatInstance.billableInstances?.length || 0 }})</span>
                                  </span>
                                </div>

                                <div v-show="isBillablesExpanded(formatInstance.format_instance_id)">
                                <v-simple-table dense rounded class="billables-table">
                                  <template v-slot:default>
                                    <thead>
                                      <tr>
                                        <th class="text-left">{{ $t('rate_card_item') }}</th>
                                        <th class="text-center" style="width: 100px">{{ $t('quantity_short') }}</th>
                                        <th v-if="showPriceFields" class="text-right" style="width: 120px">{{ $t('unit_price') }}</th>
                                        <th v-if="showPriceFields" class="text-right" style="width: 120px">{{ $t('total') }}</th>
                                        <th style="width: 40px" v-if="!effectiveReadonly"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="billable in formatInstance.billableInstances" :key="billable.billable_instance_id">
                                        <td>{{ billable.billable_title }}</td>
                                        <td class="text-center px-1">
                                          <v-text-field
                                            v-if="!effectiveReadonly"
                                            :value="billable.quantity"
                                            type="number"
                                            dense
                                            hide-details
                                            class="centered-input"
                                            min="1"
                                            step="1"
                                            @input="updateBillableQuantity(billable, formatInstance, $event)"
                                            @blur="saveBillableQuantity(billable)"
                                          >
                                          </v-text-field>
                                          <span v-else>
                                            {{ billable.quantity }}
                                          </span>
                                        </td>
                                        <td v-if="showPriceFields" class="text-right px-1">
                                          <v-text-field
                                            v-if="!effectiveReadonly && canWritePrice"
                                            :value="billable.price"
                                            type="number"
                                            dense
                                            hide-details
                                            class="text-right"
                                            prefix="R$"
                                            min="0.01"
                                            step="0.01"
                                            @input="updateBillablePrice(billable, formatInstance, $event)"
                                            @blur="saveBillablePrice(billable)"
                                          ></v-text-field>
                                          <span v-else>{{ formatCurrency(billable.price) }}</span>
                                        </td>
                                        <td v-if="showPriceFields" class="text-right font-weight-medium">{{ formatCurrency(billable.quantity * billable.price) }}</td>
                                        <td class="text-center" v-if="!effectiveReadonly">
                                          <v-btn icon @click="deleteBillableInstance(billable.billable_instance_id, formatInstance)">
                                            <v-icon>mdi-close</v-icon>
                                          </v-btn>
                                        </td>
                                      </tr>
                                    </tbody>
                                    <tfoot v-if="showPriceFields">
                                      <tr>
                                        <td colspan="2"></td>
                                        <td></td>
                                        <td class="text-right font-weight-bold primary--text">
                                          {{ formatCurrency(getFormatTotal(formatInstance)) }}
                                        </td>
                                        <td v-if="!effectiveReadonly"></td>
                                      </tr>
                                    </tfoot>
                                  </template>
                                </v-simple-table>

                                <v-btn text color="primary" class="mt-2" @click="openAddBillableDialog(formatInstance)" :disabled="effectiveReadonly">
                                  <v-icon left>mdi-plus</v-icon>
                                  {{ $t('add_billable') }}
                                </v-btn>
                                </div>
                              </div>

                              <!-- Asset Management Section -->
                              <FormatAssetManagement
                                v-if="formatInstance.format_instance_id"
                                :formatInstance="formatInstance"
                                :job-id="effectiveJobId"
                                :readonly="effectiveReadonly"
                                :can-edit-markup="canEditMarkup"
                                :content-root-node-id="contentRootNodeId"
                                @asset-uploaded="handleAssetUploaded"
                              />
                            </div>
                          </v-expand-transition>
                        </div>
                      </v-card>

                    <div v-if="!baseInstance.formatInstances || baseInstance.formatInstances.length === 0" class="text-center py-4 grey--text text--lighten-1">
                      {{ $t('no_formats_added') }}
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </v-card>
          </v-form>
        </div>

        <v-card-actions v-if="!embedded" class="pa-4">
          <v-spacer></v-spacer>
          <v-btn v-if="!isCreationMode" outlined color="primary" @click="openSaveAsPresetDialog" :disabled="effectiveReadonly || baseInstances.length === 0">
            <v-icon left>mdi-content-save</v-icon>
            {{ $t('save_as_preset') }}
          </v-btn>
          <v-btn color="primary" @click="saveAll" :loading="saving" :disabled="effectiveReadonly">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>

    <!-- Add Deliverable Dialog -->
    <v-dialog v-model="addDeliverableDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('add_deliverable') }}</v-card-title>
        <v-card-text>
          <v-form ref="addDeliverableForm" v-model="addDeliverableFormValid">
            <v-text-field
              ref="newDeliverableTitle"
              v-model="newDeliverable.base_title"
              :label="$t('title')"
              outlined
              dense
              :rules="[v => !!v || $t('field_required')]"
            ></v-text-field>
            <v-autocomplete
              v-model="newDeliverable.deliverable_id"
              :items="deliverableOptions"
              :label="$t('deliverable')"
              item-text="deliverable_title"
              item-value="deliverable_id"
              outlined
              dense
              :rules="[v => !!v || $t('field_required')]"
              @change="onDeliverableSelected"
            ></v-autocomplete>
            <v-text-field
              v-model="newDeliverable.format_desc"
              :label="$t('format_label')"
              outlined
              dense
              :rules="[v => !!v || $t('field_required')]"
            ></v-text-field>
            <v-autocomplete
              v-model="newDeliverable.rate_card_id"
              :items="rateCardOptions"
              :label="$t('rate_card_item')"
              item-text="rate_card_title"
              item-value="rate_card_id"
              outlined
              dense
              clearable
            ></v-autocomplete>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addDeliverableDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="createBaseInstance" :loading="saving" :disabled="!addDeliverableFormValid">{{ $t('add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Format Dialog -->
    <v-dialog v-model="addFormatDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('add_formats') }}</v-card-title>
        <v-card-text>
          <v-form ref="addFormatForm" v-model="addFormatFormValid">
            <p class="text-body-2 mb-3">{{ $t('add_formats_description') }}</p>
            <v-textarea
              v-model="newFormat.formatsText"
              :label="$t('formats')"
              outlined
              dense
              rows="5"
              :placeholder="$t('formats_example')"
              :rules="[v => !!v || $t('field_required')]"
            ></v-textarea>
            <v-autocomplete
              v-model="newFormat.status_id"
              :items="statusOptions"
              :label="$t('initial_status')"
              item-text="status_title"
              item-value="status_id"
              outlined
              dense
              :rules="[v => !!v || $t('field_required')]"
            ></v-autocomplete>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addFormatDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="createFormatInstances" :loading="saving" :disabled="!addFormatFormValid">{{ $t('add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Duplicate Base Instance Dialog -->
    <v-dialog v-model="duplicateBaseInstanceDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('duplicate_deliverable') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="duplicateBaseInstance.base_title"
            :label="$t('new_title')"
            outlined
            dense
            :rules="[v => !!v || $t('field_required')]"
            autofocus
          ></v-text-field>
          <v-alert type="info" dense text class="mt-2">
            {{ $t('duplicate_base_instance_info') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="duplicateBaseInstanceDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="duplicateBaseInstanceConfirm" :loading="saving">
            <v-icon left>mdi-content-duplicate</v-icon>
            {{ $t('duplicate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Duplicate Format Instance Dialog -->
    <v-dialog v-model="duplicateFormatInstanceDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('duplicate_format') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="duplicateFormatInstance.format_desc"
            :label="$t('format')"
            outlined
            dense
            :rules="[v => !!v || $t('field_required')]"
          ></v-text-field>
          <v-text-field
            v-model="duplicateFormatInstance.format_title"
            :label="$t('new_title')"
            outlined
            dense
            :rules="[v => !!v || $t('field_required')]"
            autofocus
          ></v-text-field>
          <v-text-field
            v-model.number="duplicateFormatInstance.quantity"
            :label="$t('quantity')"
            type="number"
            min="1"
            step="1"
            outlined
            dense
            :rules="[v => v > 0 || $t('quantity_must_be_positive')]"
          ></v-text-field>
          <v-alert type="info" dense text class="mt-2">
            {{ $t('duplicate_format_instance_info') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="duplicateFormatInstanceDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="duplicateFormatInstanceConfirm" :loading="saving">
            <v-icon left>mdi-content-duplicate</v-icon>
            {{ $t('duplicate') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save Formats as Preset Dialog -->
    <v-dialog v-model="saveFormatsAsPresetDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('save_formats_as_preset') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="formatPreset.preset_name"
            :label="$t('preset_name')"
            outlined
            dense
            :error-messages="formatPresetErrors.preset_name"
            autofocus
          ></v-text-field>
          <v-alert type="info" dense text class="mt-2">
            {{ $t('save_formats_preset_info') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="saveFormatsAsPresetDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="success" text @click="saveFormatsAsPresetConfirm" :loading="saving" :disabled="!formatPreset.preset_name">
            <v-icon left>mdi-content-save</v-icon>
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save as Preset Dialog -->
    <v-dialog v-model="saveAsPresetDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('save_as_preset') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="servicePreset.preset_name"
            :label="$t('preset_name')"
            outlined
            dense
            :error-messages="servicePresetErrors.preset_name"
            autofocus
          ></v-text-field>
          <v-alert type="info" dense text class="mt-2">
            {{ $t('save_service_preset_info') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="saveAsPresetDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="success" text @click="saveAsPresetConfirm" :loading="saving" :disabled="!servicePreset.preset_name">
            <v-icon left>mdi-content-save</v-icon>
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Billable Dialog -->
    <v-dialog v-model="addBillableDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t('add_billable_instance') }}</v-card-title>
        <v-card-text>
          <v-form ref="addBillableForm" v-model="addBillableFormValid">
            <v-autocomplete
              v-model="newBillable.billable_id"
              :items="billableOptions"
              :label="$t('billable')"
              item-text="billable_title"
              item-value="billable_id"
              outlined
              dense
              @change="onBillableSelected"
            ></v-autocomplete>
            <v-text-field
              v-model="newBillable.billable_title"
              :label="$t('title')"
              outlined
              dense
              :rules="[v => !!v || $t('field_required')]"
            ></v-text-field>
            <v-text-field
              v-model.number="newBillable.quantity"
              :label="$t('quantity')"
              type="number"
              min="1"
              step="1"
              outlined
              dense
              :rules="[v => v > 0 || $t('quantity_must_be_positive')]"
            ></v-text-field>
            <v-text-field
              v-model="newBillable.unit_label"
              :label="$t('unit')"
              outlined
              dense
              :rules="[v => !!v || $t('field_required')]"
            ></v-text-field>
            <v-text-field
              v-if="canWritePrice"
              v-model.number="newBillable.price"
              :label="$t('price')"
              type="number"
              min="0.01"
              step="0.01"
              outlined
              dense
              prefix="R$"
              :rules="[v => v > 0 || $t('price_must_be_positive')]"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addBillableDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" text @click="createBillableInstance" :loading="saving" :disabled="!addBillableFormValid">{{ $t('add') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Load Base Group Presets Dialog -->
    <v-dialog v-model="loadBaseGroupPresetsDialog" max-width="800px">
      <v-card>
        <v-card-title>{{ $t('load_deliverable_preset') }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedBaseGroup"
            :items="baseGroupOptions"
            :label="$t('select_group')"
            item-text="base_group_title"
            item-value="base_group_id"
            outlined
            dense
            @change="showBaseGroupConfirmation"
            :loading="loadingGroups"
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="loadBaseGroupPresetsDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Load Format Group Presets Dialog -->
    <v-dialog v-model="loadFormatGroupPresetsDialog" max-width="800px">
      <v-card>
        <v-card-title>{{ $t('load_format_preset') }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedFormatGroup"
            :items="formatGroupOptions"
            :label="$t('select_group')"
            item-text="format_group_title"
            item-value="format_group_id"
            outlined
            dense
            @change="showFormatGroupConfirmation"
            :loading="loadingGroups"
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="loadFormatGroupPresetsDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Confirm Load Base Group Dialog -->
    <v-dialog v-model="confirmLoadBasePresetDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-download</v-icon>
          {{ $t('confirm_load_preset') || 'Confirm Load Preset Group' }}
        </v-card-title>
        <v-card-text>
          <v-alert type="info" dense text class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2">{{ selectedGroupToLoad?.base_group_title || selectedGroupToLoad?.format_group_title }}</div>
            <div class="text-body-2">
              {{ $t('preset_group_will_be_added') || 'All deliverables from this preset group will be added with their formats and billable items.' }}
            </div>
          </v-alert>
          <div class="text-body-2 grey--text">
            {{ $t('preset_group_confirmation_info') || 'This will load all content from the preset group including all deliverables, formats, and billable items.' }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmLoadBasePresetDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" @click="confirmLoadBaseGroup" :loading="saving">
            <v-icon left>mdi-download</v-icon>
            {{ $t('load') || 'Load' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Load Format Group Dialog -->
    <v-dialog v-model="confirmLoadFormatPresetDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-download</v-icon>
          {{ $t('confirm_load_preset') || 'Confirm Load Format Group' }}
        </v-card-title>
        <v-card-text>
          <v-alert type="info" dense text class="mb-4">
            <div class="text-subtitle-2 font-weight-bold mb-2">{{ selectedGroupToLoad?.format_group_title }}</div>
            <div class="text-body-2">
              {{ $t('preset_format_group_will_be_added') || 'All formats from this preset group will be added with their billable items.' }}
            </div>
          </v-alert>
          <div class="text-body-2 grey--text">
            {{ $t('preset_format_group_confirmation_info') || 'This will load all formats and billable items from the preset group.' }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmLoadFormatPresetDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" @click="confirmLoadFormatGroup" :loading="saving">
            <v-icon left>mdi-download</v-icon>
            {{ $t('load') || 'Load' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          {{ $t('confirm') }}
        </v-card-title>
        <v-card-text>
          <div class="text-body-1">
            <span v-if="confirmDeleteType === 'base'">{{ $t('confirm_delete_base_instance') }}</span>
            <span v-if="confirmDeleteType === 'format'">{{ $t('confirm_delete_format_instance') }}</span>
            <span v-if="confirmDeleteType === 'billable'">{{ $t('confirm_delete_billable_instance') }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" text @click="confirmDelete">
            <v-icon left>mdi-delete</v-icon>
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import JobMutations from '@/store/job/mutations-types'
import AppActions from '@/store/app/actions-types'
import FormatInstanceAPI from '@/services/api/business/format_instance'
import BillableInstanceAPI from '@/services/api/business/billable_instance'
import DeliverableAPI from '@/services/api/business/deliverable'
import BillableAPI from '@/services/api/business/billable'
import StatusAPI from '@/services/api/business/status'
import RateCardAPI from '@/services/api/business/rate_card'
import JobAPI from '@/services/api/job/job'
import JobDefinitionAPI from '@/services/api/job/job_definition'
import JobDeliverableAPI from '@/services/api/job/deliverable'
import PresetAPI from '@/services/api/business/preset'
import AclAPI from '@/services/api/business/acl'
import PoolACLAPI from '@/services/api/job/pool_acl'
import LaneAPI from '@/services/api/job/lane'
import MarkdownEditor from '@/components/MarkdownEditor.vue'
import FormatAssetManagement from '@/components/job/FormatAssetManagement.vue'

export default {
  name: 'JobDeliverableManagement',

  components: {
    MarkdownEditor,
    FormatAssetManagement
  },

  props: {
    // Job ID - optional in creation mode
    jobId: {
      type: [String, Number],
      default: null
    },
    // Job data object - used in creation mode when no jobId exists
    jobData: {
      type: Object,
      default: () => ({})
    },
    // Whether the component is in readonly mode
    readonly: {
      type: Boolean,
      default: false
    },
    // Whether the component is embedded in another component (like Job.vue)
    embedded: {
      type: Boolean,
      default: false
    },
    // Customer ID - required for loading deliverables, statuses, etc.
    customerId: {
      type: [String, Number],
      default: null
    },
    // Service ID - optional, used when creating base instances
    serviceId: {
      type: [String, Number],
      default: null
    },
    // App ID from route - used to get app configuration
    appId: {
      type: [String, Number],
      default: null
    },
    // Content App ID from route - for DAM operations
    contentAppId: {
      type: [String, Number],
      default: null
    },
    // Business App ID from route
    businessAppId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      saving: false,
      initialized: false,
      localJobData: {},
      loadedJobDefinition: null,
      inheritedServiceId: null,
      serviceData: null,
      baseInstances: [],
      deliverableOptions: [],
      billableOptions: [],
      statusOptions: [],
      rateCardOptions: [],

      viewMode: 'list',
      expandedDeliverables: {},
      expandedFormats: {},
      expandedBillables: {},

      // Form validation
      formValid: false,

      // Validation rules
      rules: {
        required: v => !!v || this.$t('field_required'),
        requiredText: v => (!!v && v.trim() !== '') || this.$t('field_required'),
        positiveNumber: v => (v > 0) || this.$t('quantity_must_be_positive'),
        positivePrice: v => (v >= 0) || this.$t('price_must_be_positive')
      },

      // Temporary ID counters for new instances (negative numbers to distinguish from real IDs)
      tempBaseInstanceId: -1,
      tempFormatInstanceId: -1,
      tempBillableInstanceId: -1,

      addDeliverableDialog: false,
      addDeliverableFormValid: false,
      newDeliverable: {
        base_title: '',
        deliverable_id: null,
        rate_card_id: null,
        format_desc: ''
      },

      addFormatDialog: false,
      addFormatFormValid: false,
      selectedBaseInstance: null,
      newFormat: {
        formatsText: '',
        status_id: null
      },

      addBillableDialog: false,
      addBillableFormValid: false,
      selectedFormatInstance: null,
      newBillable: {
        billable_id: null,
        billable_title: '',
        quantity: 1,
        unit_label: '',
        price: 0
      },

      duplicateBaseInstanceDialog: false,
      duplicateBaseInstance: {
        base_title: '',
        sourceBaseInstance: null
      },

      duplicateFormatInstanceDialog: false,
      duplicateFormatInstance: {
        format_desc: '',
        format_title: '',
        quantity: 1,
        sourceFormatInstance: null,
        sourceBaseInstance: null
      },

      saveFormatsAsPresetDialog: false,
      formatPreset: {
        preset_name: '',
        sourceBaseInstance: null
      },
      formatPresetErrors: {
        preset_name: []
      },

      saveAsPresetDialog: false,
      servicePreset: {
        preset_name: ''
      },
      servicePresetErrors: {
        preset_name: []
      },

      // Preset loading dialogs and data
      loadBaseGroupPresetsDialog: false,
      loadFormatGroupPresetsDialog: false,
      baseGroupOptions: [],
      formatGroupOptions: [],
      basePresetOptions: [],
      formatPresetOptions: [],
      selectedBaseGroup: null,
      selectedFormatGroup: null,
      selectedBasePreset: null,
      selectedFormatPreset: null,
      loadingGroups: false,
      loadingPresets: false,
      targetBaseInstanceForFormat: null,

      // Confirmation dialogs for preset loading
      confirmLoadBasePresetDialog: false,
      confirmLoadFormatPresetDialog: false,
      selectedPresetToLoad: null,
      selectedGroupToLoad: null,

      // Business ACL constants (from business API)
      ACL_READ_PRICE: 8,   // 1000 in binary
      ACL_WRITE_PRICE: 16, // 10000 in binary

      // Job ACL constants (from job API)
      ACL_EDIT_DELIVERABLES: 128, // Edit deliverables permission
      ACL_EDIT_MARKUP: 512, // Markup editing permission

      // User ACLs from different APIs
      businessAcl: 0, // From business API - for price permissions
      jobAcl: 0,      // From job API - for markup permissions

      // Confirmation dialogs
      confirmDeleteDialog: false,
      confirmDeleteType: '', // 'base', 'format', 'billable'
      confirmDeleteTarget: null,
      confirmDeleteContext: null
    }
  },

  computed: {
    ...mapState('job', {
      storeDeliverables: state => state.tempDeliverables
    }),

    // Effective job ID - from prop or route params
    effectiveJobId() {
      return this.jobId || this.$route.params.jobId || null
    },

    // Check if component is in creation mode (no job ID yet)
    isCreationMode() {
      return !this.effectiveJobId
    },

    // Check if user has read price permission (from business ACL)
    canReadPrice() {
      return (this.businessAcl & this.ACL_READ_PRICE) === this.ACL_READ_PRICE
    },

    // Check if user has write price permission (from business ACL)
    canWritePrice() {
      return (this.businessAcl & this.ACL_WRITE_PRICE) === this.ACL_WRITE_PRICE
    },

    // User has no price privilege at all (neither read nor write).
    // In this case the price must NOT be set on the client; the billable
    // must be referenced (billable_id) so the API reads the billable and
    // fills the price in the billable_instance.
    hasNoPricePrivilege() {
      return !this.canReadPrice && !this.canWritePrice
    },


    // Check if user can edit markup (from job ACL)
    canEditMarkup() {
      return (this.jobAcl & this.ACL_EDIT_MARKUP) === this.ACL_EDIT_MARKUP
    },

    // Check if user can edit deliverables (from job ACL).
    // In creation mode there is no pool ACL loaded yet, so editing is allowed.
    canEditDeliverables() {
      if (this.isCreationMode) {
        return true
      }

      return (this.jobAcl & this.ACL_EDIT_DELIVERABLES) === this.ACL_EDIT_DELIVERABLES
    },

    // The API marks jobs that cannot be edited as readonly.
    isJobReadonly() {
      return !!(this.localJobData?.readonly || this.jobData?.readonly)
    },

    // The component becomes read-only when explicitly requested via prop,
    // when the job is marked readonly, or when the user only has view/read
    // permission over deliverables.
    effectiveReadonly() {
      return this.readonly || this.isJobReadonly || !this.canEditDeliverables
    },

    canAddDeliverable() {
      return !this.effectiveReadonly && !!this.effectiveCustomerId
    },

    // Show price fields only if user can at least read prices
    showPriceFields() {
      return this.canReadPrice || this.canWritePrice
    },

    // Price fields are editable only if user can write prices
    priceFieldsReadonly() {
      return !this.canWritePrice
    },

    appConfig() {
      const selectedJobApp = this.$store.state.app.selectedJobApp
      if (!selectedJobApp) return {}

      const appInstance = this.$store.getters['app/getApplicationInstanceById'](selectedJobApp)
      if (!appInstance || !appInstance.config) return {}

      try {
        return typeof appInstance.config === 'string' ? JSON.parse(appInstance.config) : appInstance.config
      } catch (e) {
        console.error('Error parsing app config:', e)
        return {}
      }
    },

    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    },

    totalFormats() {
      return this.baseInstances.reduce((sum, base) => {
        return sum + (base.formatInstances?.length || 0)
      }, 0)
    },

    allDeliverablesExpanded() {
      return this.baseInstances.length > 0 &&
        this.baseInstances.every(base => this.expandedDeliverables[base.base_instance_id])
    },

    totalValue() {
      return this.baseInstances.reduce((sum, base) => {
        return sum + this.getBaseInstanceTotal(base)
      }, 0)
    },

    // Effective customer ID - from prop or job data
    effectiveCustomerId() {
      return this.customerId || this.localJobData?.customer_id || this.jobData?.customer_id || null
    },

    // Effective service ID
    effectiveServiceId() {
      return this.serviceId || this.localJobData?.job_services?.[0]?.service_id || this.jobData?.job_services?.[0]?.service_id || this.inheritedServiceId || null
    },

    jobDefinitionId() {
      return this.localJobData?.job_definition_id || this.jobData?.job_definition_id || null
    },

    jobDefinition() {
      return this.$store.state.job.jobDefinitions.find(
        definition => definition.job_definition_id == this.jobDefinitionId
      ) || (
        this.loadedJobDefinition?.job_definition_id == this.jobDefinitionId
          ? this.loadedJobDefinition
          : null
      )
    },

    // Content root node ID for file selection from DAM
    // Existing jobs should start inside their own content folder.
    contentRootNodeId() {
      const jobFolder = this.localJobData?.job_folder_id || this.jobData?.job_folder_id
      const jobSpecificFallback = (
        this.localJobData?.ref_folder_id ||
        this.jobData?.ref_folder_id ||
        this.localJobData?.mat_folder_id ||
        this.jobData?.mat_folder_id
      )
      const definitionRoot = this.jobDefinition?.job_root_destination_node_id

      return jobFolder || jobSpecificFallback || definitionRoot || null
    }
  },

  watch: {
    jobDefinitionId: {
      immediate: true,
      async handler(jobDefinitionId) {
        this.loadedJobDefinition = null
        if (!jobDefinitionId || this.jobDefinition) return

        try {
          const response = await JobDefinitionAPI.getJobDefinition(jobDefinitionId)
          if (this.jobDefinitionId == jobDefinitionId) {
            this.loadedJobDefinition = response.data
          }
        } catch (error) {
          console.error('Error loading job definition:', error)
        }
      }
    },

    // Watch jobData prop for external updates
    jobData: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.localJobData = { ...this.localJobData, ...newVal }
        }
      }
    },

    // Watch baseInstances and sync to Vuex store in creation mode
    baseInstances: {
      deep: true,
      handler() {
        if (this.isCreationMode && this.baseInstances.length >= 0) {
          // Sync to Vuex store so state is preserved when switching views
          this.$store.commit(`job/${JobMutations.SET_TEMP_DELIVERABLES}`, this.getDeliverablesData())
        }
      }
    },

    // Watch store deliverables for external updates (from Job component)
    storeDeliverables: {
      deep: true,
      handler(newVal) {
        // Only sync from store in creation mode and if we have data
        if (this.isCreationMode && newVal && newVal.length > 0) {
          // Avoid infinite loops by checking if data is actually different
          const currentData = JSON.stringify(this.getDeliverablesData())
          const newData = JSON.stringify(newVal)
          if (currentData !== newData) {
            this.setDeliverablesData(newVal)
          }
        }
      }
    },

    effectiveCustomerId: {
      async handler(newValue, oldValue) {
        if (!this.initialized || String(newValue || '') === String(oldValue || '')) {
          return
        }

        const hadPreviousCustomer = oldValue !== undefined && oldValue !== null && oldValue !== ''
        const clearInstances = this.isCreationMode && hadPreviousCustomer

        await this.reloadCustomerScopedOptions({ clearInstances })
      }
    }
  },

  mounted() {
    this.initialize()
  },

  methods: {
    async initialize() {
      this.loading = true
      try {
        // Merge prop jobData into local state
        this.localJobData = { ...this.jobData }

        if (this.isCreationMode) {
          // Creation mode - load options but don't load deliverables from API
          await Promise.all([
            this.loadDeliverables(),
            this.loadBillables(),
            this.loadStatuses(),
            this.loadRateCards(),
            this.loadBusinessAcl(),
            this.loadJobAcl(),
          ])

          // In creation mode, load from Vuex store if available
          if (this.storeDeliverables && this.storeDeliverables.length > 0) {
            this.setDeliverablesData(this.storeDeliverables)
          } else {
            this.baseInstances = []
          }
        } else {
          await this.loadJobDataFromAPI()
          await Promise.all([
            this.loadBaseInstances(),
            this.loadDeliverables(),
            this.loadBillables(),
            this.loadStatuses(),
            this.loadRateCards(),
            this.loadBusinessAcl(),
            this.loadJobAcl(),
          ])
        }
      } catch (error) {
        console.error('Error initializing deliverable management:', error)
      } finally {
        this.loading = false
        this.initialized = true
        this.openRequestedHeaderAction()
      }
    },

    openRequestedHeaderAction() {
      const action = this.$route.query.action

      if (action === 'add') {
        this.openAddDeliverableDialog()
      } else if (action === 'preset') {
        this.openLoadBaseGroupPresetsDialog()
      }
    },

    async reloadCustomerScopedOptions({ clearInstances = false } = {}) {
      this.clearCustomerScopedOptions(clearInstances)

      if (!this.effectiveCustomerId) {
        return
      }

      await Promise.all([
        this.loadDeliverables(),
        this.loadBillables(),
        this.loadStatuses(),
        this.loadBusinessAcl()
      ])
    },

    clearCustomerScopedOptions(clearInstances = false) {
      this.deliverableOptions = []
      this.billableOptions = []
      this.statusOptions = []
      this.businessAcl = 0

      if (clearInstances) {
        this.baseInstances = []
        this.expandedDeliverables = {}
        this.expandedFormats = {}
        this.$emit('deliverables-changed', [])
      }
    },

    async loadJobDataFromAPI() {
      if (!this.effectiveJobId) return

      try {
        const jobResponse = await JobAPI.getJob(this.effectiveJobId)
        this.localJobData = { ...this.localJobData, ...jobResponse.data }
        await this.loadInheritedServiceId()
        console.log('Loaded job data:', this.localJobData)
      } catch (error) {
        console.error('Error loading job data:', error)
      }
    },

    async loadInheritedServiceId() {
      this.inheritedServiceId = null
      if (this.effectiveServiceId) return

      // Subtasks have no job_service relation of their own; use the nearest ancestor's service.
      let parentJobId = this.localJobData.parent_job_id
      const visitedJobIds = new Set([String(this.effectiveJobId)])

      while (parentJobId && !visitedJobIds.has(String(parentJobId))) {
        visitedJobIds.add(String(parentJobId))
        const response = await JobAPI.getJob(parentJobId)
        const parentJob = response.data
        this.inheritedServiceId = parentJob?.job_services?.[0]?.service_id || null
        if (this.inheritedServiceId) return
        parentJobId = parentJob?.parent_job_id
      }
    },

    async loadDeliverables() {
      if (!this.effectiveCustomerId) return
      try {
        const response = await DeliverableAPI.getAllDeliverablesByCustomer(this.effectiveCustomerId, {
          order: 'deliverable_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.deliverableOptions = response.data.items || []

        // Load billables for each deliverable
        await this.loadBillablesForDeliverables()
      } catch (error) {
        console.error('Error loading deliverables:', error)
      }
    },

    async loadBillablesForDeliverables() {
      // Load billables for each deliverable so they can be used when creating formats
      for (const deliverable of this.deliverableOptions) {
        try {
          const response = await BillableAPI.getAllBillablesByDeliverable(deliverable.deliverable_id, {
            order: 'billable_title',
            direction: 'ASC',
            offset: 0,
            limit: 1000
          })

          this.$set(deliverable, 'billables', response.data.items || [])
        } catch (error) {
          console.error(`Error loading billables for deliverable ${deliverable.deliverable_id}:`, error)
          this.$set(deliverable, 'billables', [])
        }
      }
    },

    async loadBillables() {
      if (!this.effectiveCustomerId) return
      try {
        const response = await BillableAPI.getAllBillablesByCustomer(this.effectiveCustomerId, {
          order: 'billable_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.billableOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading billables:', error)
      }
    },

    async loadStatuses() {
      if (!this.effectiveCustomerId) return
      try {
        const response = await StatusAPI.getAllStatusesByCustomer(this.effectiveCustomerId, {
          order: 'status_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.statusOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading statuses:', error)
      }
    },

    async loadRateCards() {
      if (!this.appTenantId) {
        console.warn('No tenant_id found in app config')
        return
      }

      try {
        const response = await RateCardAPI.getAllRateCardsByTenant(
          this.appTenantId,
          {
            order: 'rate_card_title',
            direction: 'ASC',
            offset: 0,
            limit: 1000
          }
        )

        this.rateCardOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading rate cards:', error)
      }
    },

    async loadBusinessAcl() {
      if (!this.effectiveCustomerId) return

      try {
        const response = await AclAPI.getMyAcls({
          customer_id: this.effectiveCustomerId
        })

        // Get the ACL items for the current user
        const aclItems = response.data.items || response.data || []

        // Filter ACLs for this customer (there can be multiple)
        const customerAcls = Array.isArray(aclItems)
          ? aclItems.filter(acl => acl.customer_id === this.effectiveCustomerId)
          : (aclItems.customer_id === this.effectiveCustomerId ? [aclItems] : [])

        // Combine all ACLs using bitwise OR to get all permissions
        this.businessAcl = customerAcls.reduce((combined, acl) => {
          return combined | (acl.access_level || 0)
        }, 0)
      } catch (error) {
        console.error('Error loading business ACL:', error)
        this.businessAcl = 0 // Default to no permissions on error
      }
    },

    async loadJobAcl() {
      // Get the job's lane_id
      const laneId = this.localJobData?.lane_id

      if (!laneId) {
        console.warn('No lane_id found for job, cannot load pool ACLs')
        this.jobAcl = 0
        return
      }

      try {
        // Get the lane to find its pool_id
        const laneResponse = await LaneAPI.getLane(laneId)
        const lane = laneResponse.data
        const poolId = lane?.pool_id

        if (!poolId) {
          console.warn('No pool_id found for lane, cannot load pool ACLs')
          this.jobAcl = 0
          return
        }

        // Get pool ACLs from user's groups
        const result = await PoolACLAPI.getPoolACLsFromUserGroup()
        const allPoolAcls = result.data.items || []

        // Filter ACLs for this specific pool
        const poolAcls = allPoolAcls.filter(acl => acl.pool_id == poolId)

        // Combine all ACLs for this pool using bitwise OR
        this.jobAcl = poolAcls.reduce((combined, acl) => {
          return combined | (parseInt(acl.acl) || 0)
        }, 0)
      } catch (error) {
        console.error('Error loading pool ACL:', error)
        this.jobAcl = 0 // Default to no permissions on error
      }
    },

    async loadBaseInstances() {
      if (!this.effectiveJobId) return

      try {
        // Use the job deliverable endpoint to get nested data
        const response = await JobDeliverableAPI.getJobDeliverables(this.effectiveJobId)

        const deliverables = response.data || []

        // Check if any billable instance has a price field to determine privilege
        let hasPriceField = false

        // Transform the nested structure to match the component's data model
        this.baseInstances = deliverables.map(item => {
          const baseInstance = item.base_instance

          // Map format instances with their nested billable instances
          baseInstance.formatInstances = (item.format_instances || []).map(formatItem => {
            console.log('Processing format instance:', formatItem)
            const formatInstance = formatItem.format_instance

            // Map 'format' from API to 'format_desc' for component use
            if (formatInstance.format !== undefined) {
              formatInstance.format_desc = formatInstance.format
              delete formatInstance.format
            }

            // Map billable instances
            formatInstance.billableInstances = (formatItem.billable_instances || []).map(billableItem => {
              // Check if this billable has a price field
              if (Object.prototype.hasOwnProperty.call(billableItem, 'price')) {
                hasPriceField = true
              }

              return {
                ...billableItem,
                // Set price to 0 if not provided (no privilege)
                price: billableItem.price !== undefined ? billableItem.price : 0
              }
            })

            // Include job_deliver data if needed
            formatInstance.job_deliver = formatItem.job_deliver || null
            formatInstance.folder_node_id = formatItem.format_instance.folder_node_id || null

            return formatInstance
          })

          return baseInstance
        })

        // Update price access flags based on API response
        // If no billable instances have price field, user has no price privilege
        this.hasPriceAccess = hasPriceField
        this.hasPriceWriteAccess = hasPriceField
      } catch (error) {
        console.error('Error loading base instances:', error)
      }
    },

    async loadFormatInstances(baseInstance) {
      // This method is kept for backward compatibility with other operations
      try {
        const response = await FormatInstanceAPI.getFormatInstancesByBaseInstance(baseInstance.base_instance_id, {
          order: 'format_instance_id',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.$set(baseInstance, 'formatInstances', response.data.items || [])

        await Promise.all(
          (baseInstance.formatInstances || []).map(format => this.loadBillableInstances(format))
        )
      } catch (error) {
        console.error('Error loading format instances:', error)
      }
    },

    async loadBillableInstances(formatInstance) {
      // This method is kept for backward compatibility with other operations
      try {
        const response = await BillableInstanceAPI.getBillableInstancesByFormatInstance(formatInstance.format_instance_id, {
          order: 'billable_instance_id',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.$set(formatInstance, 'billableInstances', response.data.items || [])
      } catch (error) {
        console.error('Error loading billable instances:', error)
      }
    },

    toggleDeliverable(baseInstanceId) {
      this.$set(this.expandedDeliverables, baseInstanceId, !this.expandedDeliverables[baseInstanceId])
    },

    toggleFormat(formatInstanceId) {
      this.$set(this.expandedFormats, formatInstanceId, !this.expandedFormats[formatInstanceId])
    },

    isBillablesExpanded(formatInstanceId) {
      return this.expandedBillables[formatInstanceId] !== false
    },

    toggleBillables(formatInstanceId) {
      this.$set(this.expandedBillables, formatInstanceId, !this.isBillablesExpanded(formatInstanceId))
    },

    toggleAll() {
      const expand = !this.allDeliverablesExpanded
      this.baseInstances.forEach(base => {
        this.$set(this.expandedDeliverables, base.base_instance_id, expand)
      })
    },

    areAllFormatsExpanded(baseInstance) {
      const formats = baseInstance.formatInstances || []
      return formats.length > 0 && formats.every(format => this.expandedFormats[format.format_instance_id])
    },

    toggleAllFormats(baseInstance) {
      const expand = !this.areAllFormatsExpanded(baseInstance)
      ;(baseInstance.formatInstances || []).forEach(format => {
        this.$set(this.expandedFormats, format.format_instance_id, expand)
      })
    },

    parseFormatDescriptions(value) {
      return (value || '')
        .split(/[;|\n]/)
        .map(format => format.trim())
        .filter(Boolean)
    },

    async openAddDeliverableDialog() {
      if (!this.effectiveCustomerId) return

      if (this.deliverableOptions.length === 0) {
        await this.reloadCustomerScopedOptions()
      }

      this.newDeliverable = { base_title: '', deliverable_id: null, rate_card_id: null, format_desc: '' }
      this.addDeliverableDialog = true
      // Wait for the dialog's lazy content before focusing, including on reopen.
      await this.$nextTick()
      await this.$nextTick()
      this.$refs.newDeliverableTitle?.focus()
    },

    onDeliverableSelected() {
      // When a deliverable is selected, pre-fill the format label with the
      // deliverable's default_format_desc (if configured).
      const deliverable = this.deliverableOptions.find(d => d.deliverable_id === this.newDeliverable.deliverable_id)
      this.newDeliverable.format_desc = deliverable?.default_format_desc || ''
    },

    createBaseInstance() {
      if (!this.newDeliverable.base_title || !this.newDeliverable.deliverable_id) return

      // Default status for new instances (waiting)
      const defaultStatusId = this.statusOptions.find(s => s.status_type === 'waiting')?.status_id || null

      // Create a new base instance in state only (not saved to API yet)
      const newBaseInstance = {
        base_instance_id: this.tempBaseInstanceId--, // Use negative ID for new instances
        service_id: this.effectiveServiceId,
        deliverable_id: this.newDeliverable.deliverable_id,
        rate_card_id: this.newDeliverable.rate_card_id,
        base_title: this.newDeliverable.base_title,
        status_id: defaultStatusId,
        base_spec: '',
        created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
        formatInstances: []
      }

      // Create default format instances using the same delimiter rules as the
      // Add Formats dialog.
      const formatDescs = this.parseFormatDescriptions(this.newDeliverable.format_desc)
      if (formatDescs.length) {
        // Get billables for this deliverable
        const deliverable = this.deliverableOptions.find(d => d.deliverable_id === newBaseInstance.deliverable_id)
        const deliverableBillables = deliverable?.billables || []

        for (const formatDesc of formatDescs) {
          // Generate title: {jobId}-{base_title}-{format}
          const jobIdPart = this.effectiveJobId || 'NEW'
          const formatTitle = `${jobIdPart}-${newBaseInstance.base_title}-${formatDesc}`

          const newFormatInstance = {
            format_instance_id: this.tempFormatInstanceId--, // Temporary negative ID
            base_instance_id: newBaseInstance.base_instance_id,
            status_id: defaultStatusId,
            format_desc: formatDesc,
            format_title: formatTitle,
            quantity: 1,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
            billableInstances: []
          }

          // Create billable instances from deliverable billables
          if (deliverableBillables.length > 0) {
            for (const deliverableBillable of deliverableBillables) {
              const newBillableInstance = {
                billable_instance_id: this.tempBillableInstanceId--,
                format_instance_id: newFormatInstance.format_instance_id,
                billable_id: deliverableBillable.billable_id,
                billable_title: deliverableBillable.billable_title,
                quantity: deliverableBillable.quantity || 1,
                unit_label: deliverableBillable.unit_label,
                price: deliverableBillable.price || 0,
                created_by_user: this.$store.state.core.auth.userData?.users_id || 1
              }

              newFormatInstance.billableInstances.push(newBillableInstance)
            }
          }

          newBaseInstance.formatInstances.push(newFormatInstance)

          // Auto-expand the new format
          this.$set(this.expandedFormats, newFormatInstance.format_instance_id, true)
        }
      }

      // Add to state
      this.baseInstances.push(newBaseInstance)

      // Auto-expand the new instance
      this.$set(this.expandedDeliverables, newBaseInstance.base_instance_id, true)

      this.addDeliverableDialog = false

      // Emit event to notify parent component
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    deleteBaseInstance(baseInstanceId) {
      this.confirmDeleteType = 'base'
      this.confirmDeleteTarget = baseInstanceId
      this.confirmDeleteDialog = true
    },

    openAddFormatInstanceDialog(baseInstance) {
      this.selectedBaseInstance = baseInstance
      this.newFormat = {
        formatsText: '',
        status_id: this.statusOptions.find(s => s.status_type === 'waiting')?.status_id || null
      }
      this.addFormatDialog = true
    },

    createFormatInstances() {
      if (!this.newFormat.formatsText || !this.newFormat.status_id) return

      const formatStrings = this.parseFormatDescriptions(this.newFormat.formatsText)

      if (formatStrings.length === 0) {
        // No valid formats - just close the dialog
        this.addFormatDialog = false
        return
      }

      // Get billables for this deliverable
      const deliverable = this.deliverableOptions.find(d => d.deliverable_id === this.selectedBaseInstance.deliverable_id)
      const deliverableBillables = deliverable?.billables || []

      // Ensure formatInstances array exists
      if (!this.selectedBaseInstance.formatInstances) {
        this.$set(this.selectedBaseInstance, 'formatInstances', [])
      }

      // Create format instance for each format (in state only)
      for (const formatString of formatStrings) {
        // Generate title: {jobId}-{base_title}-{format}
        const jobIdPart = this.effectiveJobId || 'NEW'
        const formatTitle = `${jobIdPart}-${this.selectedBaseInstance.base_title}-${formatString}`

        const newFormatInstance = {
          format_instance_id: this.tempFormatInstanceId--, // Temporary negative ID
          base_instance_id: this.selectedBaseInstance.base_instance_id,
          status_id: this.newFormat.status_id,
          format_desc: formatString,
          format_title: formatTitle,
          quantity: 1,
          created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
          billableInstances: []
        }

        // Create billable instances from deliverable billables
        if (deliverableBillables.length > 0) {
          for (const deliverableBillable of deliverableBillables) {
            const newBillableInstance = {
              billable_instance_id: this.tempBillableInstanceId--,
              format_instance_id: newFormatInstance.format_instance_id,
              billable_id: deliverableBillable.billable_id,
              billable_title: deliverableBillable.billable_title,
              quantity: deliverableBillable.quantity || 1,
              unit_label: deliverableBillable.unit_label,
              price: deliverableBillable.price || 0,
              created_by_user: this.$store.state.core.auth.userData?.users_id || 1
            }

            newFormatInstance.billableInstances.push(newBillableInstance)
          }
        }

        // Add to state
        this.selectedBaseInstance.formatInstances.push(newFormatInstance)

        // Auto-expand the new format
        this.$set(this.expandedFormats, newFormatInstance.format_instance_id, true)
      }

      this.addFormatDialog = false

      // Emit event to notify parent component
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    deleteFormatInstance(formatInstanceId) {
      this.confirmDeleteType = 'format'
      this.confirmDeleteTarget = formatInstanceId
      this.confirmDeleteDialog = true
    },

    openAddBillableDialog(formatInstance) {
      this.selectedFormatInstance = formatInstance
      this.newBillable = {
        billable_id: null,
        billable_title: '',
        quantity: 1,
        unit_label: '',
        price: 0
      }
      this.addBillableDialog = true
    },

    onBillableSelected() {
      const billable = this.billableOptions.find(b => b.billable_id === this.newBillable.billable_id)
      if (billable) {
        this.newBillable.billable_title = billable.billable_title
        this.newBillable.unit_label = billable.unit_label
        if (this.showPriceFields) {
          this.newBillable.price = billable.price
        }
      }
    },

    createBillableInstance() {
      // Price is optional when the user has no price privilege.
      if (!this.newBillable.billable_title || !this.newBillable.quantity || !this.newBillable.unit_label) return

      // When the user has no price privilege, the billable MUST be referenced so
      // the API can read the billable and fill the price in the billable_instance.
      if (this.hasNoPricePrivilege && !this.newBillable.billable_id) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('billable_reference_required'))
        return
      }

      // Create a new billable instance in state only (not saved to API yet)
      const newBillableInstance = {
        billable_instance_id: this.tempBillableInstanceId--,
        format_instance_id: this.selectedFormatInstance.format_instance_id,
        billable_id: this.newBillable.billable_id,
        billable_title: this.newBillable.billable_title,
        quantity: this.newBillable.quantity,
        unit_label: this.newBillable.unit_label,
        // Do not add a placeholder price without price access. This absence is
        // preserved when the parent serializes the item for the API.
        ...(this.showPriceFields ? { price: this.newBillable.price || 0 } : {}),
        created_by_user: this.$store.state.core.auth.userData?.users_id || 1
      }


      // Ensure billableInstances array exists
      if (!this.selectedFormatInstance.billableInstances) {
        this.$set(this.selectedFormatInstance, 'billableInstances', [])
      }

      // Add to state
      this.selectedFormatInstance.billableInstances.push(newBillableInstance)

      this.addBillableDialog = false

      // Emit event to notify parent component
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    deleteBillableInstance(billableInstanceId, formatInstance) {
      this.confirmDeleteType = 'billable'
      this.confirmDeleteTarget = billableInstanceId
      this.confirmDeleteContext = formatInstance
      this.confirmDeleteDialog = true
    },

    confirmDelete() {
      if (this.confirmDeleteType === 'base') {
        // Remove base instance from state
        const index = this.baseInstances.findIndex(b => b.base_instance_id === this.confirmDeleteTarget)
        if (index !== -1) {
          this.baseInstances.splice(index, 1)
        }
      } else if (this.confirmDeleteType === 'format') {
        // Remove format instance from state
        for (const baseInstance of this.baseInstances) {
          const index = (baseInstance.formatInstances || []).findIndex(f => f.format_instance_id === this.confirmDeleteTarget)
          if (index !== -1) {
            baseInstance.formatInstances.splice(index, 1)
            break
          }
        }
      } else if (this.confirmDeleteType === 'billable') {
        // Remove billable instance from state
        const formatInstance = this.confirmDeleteContext
        const index = (formatInstance.billableInstances || []).findIndex(b => b.billable_instance_id === this.confirmDeleteTarget)
        if (index !== -1) {
          formatInstance.billableInstances.splice(index, 1)
        }
      }

      // Close dialog and reset
      this.confirmDeleteDialog = false
      this.confirmDeleteType = ''
      this.confirmDeleteTarget = null
      this.confirmDeleteContext = null

      // Emit event to notify parent component
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    updateFormatQuantity(formatInstance, value) {
      formatInstance.quantity = parseInt(value, 10) || 1
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    updateFormatStatus(formatInstance, statusId) {
      // Update state only (will be saved on saveAll)
      formatInstance.status_id = statusId
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    updateBillableQuantity(billable, formatInstance, value) {
      billable.quantity = parseInt(value, 10) || 1
      // Trigger reactivity for total recalculation
      this.$forceUpdate()
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    saveBillableQuantity(/* billable */) {
      // This method is kept for backward compatibility but now only ensures state is updated
      // No immediate API call - will be saved on saveAll
    },

    updateBillablePrice(billable, formatInstance, value) {
      billable.price = parseFloat(value) || 0
      // Trigger reactivity for total recalculation
      this.$forceUpdate()
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    saveBillablePrice(/* billable */) {
      // This method is kept for backward compatibility but now only ensures state is updated
      // No immediate API call - will be saved on saveAll
    },

    getFormatTotal(formatInstance) {
      if (!formatInstance.billableInstances) return 0
      return formatInstance.billableInstances.reduce((sum, b) => sum + (b.quantity * b.price), 0)
    },

    getBaseInstanceAssetCount(baseInstance) {
      if (!baseInstance.formatInstances) return 0
      return baseInstance.formatInstances.reduce((sum, formatInstance) => {
        return sum + (Number(formatInstance.job_deliver?.job_deliver_asset_count) || 0)
      }, 0)
    },

    getBaseInstanceTotal(baseInstance) {
      if (!baseInstance.formatInstances) return 0
      return baseInstance.formatInstances.reduce((sum, f) => sum + this.getFormatTotal(f), 0)
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    },

    getStatusTitle(statusId) {
      const status = this.statusOptions.find(s => s.status_id === statusId)
      return status ? status.status_title : this.$t('draft')
    },

    getBaseInstanceIcon(baseInstance) {
      const deliverable = this.deliverableOptions.find(d => d.deliverable_id === baseInstance.deliverable_id)
      return deliverable?.icon || 'mdi-package-variant'
    },

    openDuplicateBaseInstanceDialog(baseInstance) {
      this.duplicateBaseInstance = {
        base_title: `${baseInstance.base_title} (Copy)`,
        sourceBaseInstance: baseInstance
      }
      this.duplicateBaseInstanceDialog = true
    },

    duplicateBaseInstanceConfirm() {
      if (!this.duplicateBaseInstance.base_title) return

      const source = this.duplicateBaseInstance.sourceBaseInstance

      // Create the new base instance in state only (not saved to API yet)
      const newBaseInstance = {
        base_instance_id: this.tempBaseInstanceId--, // Use negative ID for new instances
        service_id: source.service_id,
        deliverable_id: source.deliverable_id,
        rate_card_id: source.rate_card_id,
        base_title: this.duplicateBaseInstance.base_title,
        base_spec: source.base_spec || '',
        status_id: source.status_id,
        created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
        formatInstances: []
      }

      // Duplicate all format instances and their billables
      if (source.formatInstances && source.formatInstances.length > 0) {
        for (const formatInstance of source.formatInstances) {
          const newFormatInstance = {
            format_instance_id: this.tempFormatInstanceId--, // Temporary negative ID
            base_instance_id: newBaseInstance.base_instance_id,
            status_id: formatInstance.status_id,
            format_desc: formatInstance.format_desc,
            format_title: formatInstance.format_title,
            quantity: formatInstance.quantity,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
            billableInstances: []
          }

          // Duplicate all billable instances
          if (formatInstance.billableInstances && formatInstance.billableInstances.length > 0) {
            for (const billableInstance of formatInstance.billableInstances) {
              const newBillableInstance = {
                billable_instance_id: this.tempBillableInstanceId--,
                format_instance_id: newFormatInstance.format_instance_id,
                billable_id: billableInstance.billable_id,
                billable_title: billableInstance.billable_title,
                quantity: billableInstance.quantity,
                unit_label: billableInstance.unit_label,
                price: billableInstance.price,
                created_by_user: this.$store.state.core.auth.userData?.users_id || 1
              }

              newFormatInstance.billableInstances.push(newBillableInstance)
            }
          }

          newBaseInstance.formatInstances.push(newFormatInstance)
        }
      }

      // Add to state
      this.baseInstances.push(newBaseInstance)

      // Auto-expand the new instance
      this.$set(this.expandedDeliverables, newBaseInstance.base_instance_id, true)

      this.duplicateBaseInstanceDialog = false

      // Emit event to notify parent component
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    openDuplicateFormatInstanceDialog(formatInstance) {
      const baseInstance = this.baseInstances.find(base =>
        base.formatInstances && base.formatInstances.some(f => f.format_instance_id === formatInstance.format_instance_id)
      )

      this.duplicateFormatInstance = {
        format_desc: formatInstance.format_desc || '',
        format_title: `${formatInstance.format_title} (Copy)`,
        quantity: formatInstance.quantity || 1,
        sourceFormatInstance: formatInstance,
        sourceBaseInstance: baseInstance
      }
      this.duplicateFormatInstanceDialog = true
    },

    duplicateFormatInstanceConfirm() {
      if (!this.duplicateFormatInstance.format_desc || !this.duplicateFormatInstance.format_title || !this.duplicateFormatInstance.quantity) return

      const source = this.duplicateFormatInstance.sourceFormatInstance
      const baseInstance = this.duplicateFormatInstance.sourceBaseInstance

      // Create the new format instance in state only (not saved to API yet)
      const newFormatInstance = {
        format_instance_id: this.tempFormatInstanceId--, // Temporary negative ID
        base_instance_id: source.base_instance_id,
        status_id: source.status_id,
        format_desc: this.duplicateFormatInstance.format_desc,
        format_title: this.duplicateFormatInstance.format_title,
        quantity: this.duplicateFormatInstance.quantity,
        created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
        billableInstances: []
      }

      // Duplicate all billable instances
      if (source.billableInstances && source.billableInstances.length > 0) {
        for (const billableInstance of source.billableInstances) {
          const newBillableInstance = {
            billable_instance_id: this.tempBillableInstanceId--,
            format_instance_id: newFormatInstance.format_instance_id,
            billable_id: billableInstance.billable_id,
            billable_title: billableInstance.billable_title,
            quantity: billableInstance.quantity,
            unit_label: billableInstance.unit_label,
            price: billableInstance.price,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1
          }

          newFormatInstance.billableInstances.push(newBillableInstance)
        }
      }

      // Ensure formatInstances array exists
      if (!baseInstance.formatInstances) {
        this.$set(baseInstance, 'formatInstances', [])
      }

      // Add to state
      baseInstance.formatInstances.push(newFormatInstance)

      // Auto-expand the new format instance
      this.$set(this.expandedFormats, newFormatInstance.format_instance_id, true)

      this.duplicateFormatInstanceDialog = false

      // Emit event to notify parent component
      this.$emit('deliverables-changed', this.getDeliverablesData())
    },

    openSaveFormatsAsPresetDialog(baseInstance) {
      // Check if the base instance or any of its formats are unsaved
      const hasUnsavedBase = baseInstance.base_instance_id < 0
      const hasUnsavedFormats = baseInstance.formatInstances?.some(f => f.format_instance_id < 0) || false

      if (hasUnsavedBase || hasUnsavedFormats) {
        alert(this.$t('save_before_creating_preset') || 'Please save your changes before creating a preset.')
        return
      }

      this.formatPreset = {
        preset_name: `${baseInstance.base_title} - Formats`,
        description: '',
        sourceBaseInstance: baseInstance
      }
      this.saveFormatsAsPresetDialog = true
    },

    async saveFormatsAsPresetConfirm() {
      if (!this.formatPreset.preset_name) return

      this.saving = true
      try {
        const source = this.formatPreset.sourceBaseInstance

        // Step 1: Create or get Format Group
        let formatGroupId = null
        const formatGroupTitle = this.formatPreset.preset_name

        const formatGroupPayload = {
          customer_id: this.effectiveCustomerId,
          format_group_title: formatGroupTitle
        }

        const formatGroupResponse = await PresetAPI.createFormatGroup(formatGroupPayload)
        formatGroupId = formatGroupResponse.data.format_group_id

        // Step 2: Create Format Presets for each format instance (only if they're saved)
        for (const formatInstance of source.formatInstances || []) {
          // Skip unsaved format instances
          if (formatInstance.format_instance_id < 0) {
            console.warn('Skipping unsaved format instance:', formatInstance.format_title)
            continue
          }

          const formatPresetPayload = {
            format_group_id: formatGroupId,
            format_title: formatInstance.format_title,
            format_desc: formatInstance.format_desc,
            quantity: formatInstance.quantity,
            status_id: formatInstance.status_id,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1
          }

          const formatPresetResponse = await PresetAPI.createFormatPreset(formatPresetPayload)
          const formatPresetId = formatPresetResponse.data.format_preset_id

          // Step 3: Create Billable Presets for each billable instance
          for (const billableInstance of formatInstance.billableInstances || []) {
            const billablePresetPayload = {
              format_preset_id: formatPresetId,
              billable_title: billableInstance.billable_title,
              quantity: billableInstance.quantity,
              unit_label: billableInstance.unit_label,
              price: billableInstance.price,
              created_by_user: this.$store.state.core.auth.userData?.users_id || 1
            }

            await PresetAPI.createBillablePreset(billablePresetPayload)
          }
        }

        this.saveFormatsAsPresetDialog = false
      } catch (error) {
        console.error('Error saving formats as preset:', error)
      } finally {
        this.saving = false
      }
    },

    openSaveAsPresetDialog() {
      // Check if there are any unsaved base instances
      const hasUnsavedInstances = this.baseInstances.some(b => b.base_instance_id < 0)

      if (hasUnsavedInstances) {
        alert(this.$t('save_before_creating_preset') || 'Please save your changes before creating a preset.')
        return
      }

      this.servicePreset = {
        preset_name: this.localJobData.job_title ? `${this.localJobData.job_title} - Service` : 'New Service Preset',
        description: ''
      }
      this.saveAsPresetDialog = true
    },

    async saveAsPresetConfirm() {
      if (!this.servicePreset.preset_name) return

      this.saving = true
      try {
        // Step 1: Create Base Group
        const baseGroupPayload = {
          customer_id: this.effectiveCustomerId,
          base_group_title: this.servicePreset.preset_name
        }

        const baseGroupResponse = await PresetAPI.createBaseGroup(baseGroupPayload)
        const baseGroupId = baseGroupResponse.data.base_group_id

        // Step 2: For each base instance, create a base preset and its nested structures
        for (const baseInstance of this.baseInstances) {
          // Create base preset
          const basePresetPayload = {
            base_group_id: baseGroupId,
            base_title: baseInstance.base_title,
            base_spec: baseInstance.base_spec || '',
            status_id: baseInstance.status_id,
            deliverable_id: baseInstance.deliverable_id || null,
            rate_card_id: baseInstance.rate_card_id || null,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1
          }

          const basePresetResponse = await PresetAPI.createBasePreset(basePresetPayload)
          const basePresetId = basePresetResponse.data.base_preset_id

          // Step 3: For each format instance, create format preset
          for (const formatInstance of baseInstance.formatInstances || []) {
            const formatPresetPayload = {
              base_preset_id: basePresetId,
              format_title: formatInstance.format_title,
              format_desc: formatInstance.format_desc,
              quantity: formatInstance.quantity,
              status_id: formatInstance.status_id,
              created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
              folder_node_id: formatInstance.folder_node_id || null
            }

            const formatPresetResponse = await PresetAPI.createFormatPreset(formatPresetPayload)
            const formatPresetId = formatPresetResponse.data.format_preset_id

            // Step 4: For each billable instance, create billable preset
            for (const billableInstance of formatInstance.billableInstances || []) {
              const billablePresetPayload = {
                format_preset_id: formatPresetId,
                billable_title: billableInstance.billable_title,
                quantity: billableInstance.quantity,
                unit_label: billableInstance.unit_label,
                price: billableInstance.price,
                created_by_user: this.$store.state.core.auth.userData?.users_id || 1
              }

              await PresetAPI.createBillablePreset(billablePresetPayload)
            }
          }
        }

        this.saveAsPresetDialog = false
      } catch (error) {
        console.error('Error saving service as preset:', error)
      } finally {
        this.saving = false
      }
    },

    // Get deliverables data for saving
    getDeliverablesData() {
      return this.baseInstances.map(baseInstance => ({
        base_instance: {
          // Send null for new instances (negative IDs), otherwise send the actual ID
          base_instance_id: baseInstance.base_instance_id < 0 ? null : baseInstance.base_instance_id,
          service_id: baseInstance.service_id ?? this.effectiveServiceId,
          deliverable_id: baseInstance.deliverable_id,
          base_title: baseInstance.base_title,
          base_spec: baseInstance.base_spec || '',
          status_id: baseInstance.status_id,
          rate_card_id: baseInstance.rate_card_id || null,
          created_at: baseInstance.created_at,
          created_by_user: baseInstance.created_by_user
        },
        format_instances: (baseInstance.formatInstances || []).map(formatInstance => ({
          format_instance: {
            // Send null for new instances (negative IDs), otherwise send the actual ID
            format_instance_id: formatInstance.format_instance_id < 0 ? null : formatInstance.format_instance_id,
            // For new formats under new base instances, base_instance_id will be negative - send null
            base_instance_id: formatInstance.base_instance_id < 0 ? null : formatInstance.base_instance_id,
            status_id: formatInstance.status_id,
            format_desc: formatInstance.format_desc || '',
            format_title: formatInstance.format_title,
            quantity: formatInstance.quantity,
            created_at: formatInstance.created_at,
            created_by_user: formatInstance.created_by_user,
            folder_node_id: formatInstance.folder_node_id || null
          },
          job_deliver: formatInstance.job_deliver || {
            job_deliver_id: null,
            quantity: formatInstance.quantity || 1,
            spec: ''
          },
          billable_instances: (formatInstance.billableInstances || []).map(billableInstance => ({
            // Send null for new instances (negative IDs), otherwise send the actual ID
            billable_instance_id: billableInstance.billable_instance_id < 0 ? null : billableInstance.billable_instance_id,
            // For new billables under new formats, format_instance_id will be negative - send null
            format_instance_id: billableInstance.format_instance_id < 0 ? null : billableInstance.format_instance_id,
            billable_id: billableInstance.billable_id,
            billable_title: billableInstance.billable_title,
            quantity: billableInstance.quantity,
            unit_label: billableInstance.unit_label,
            // Without price access, omit price so the API resolves it from billable_id.
            ...(this.showPriceFields ? { price: billableInstance.price } : {}),
            created_at: billableInstance.created_at,
            created_by_user: billableInstance.created_by_user
          }))
        }))
      }))
    },

    // Set deliverables data (for loading from parent)
    setDeliverablesData(data) {
      if (!data || !Array.isArray(data)) return

      this.baseInstances = data.map(item => {
        const baseInstance = item.base_instance || item

        // Map format instances
        baseInstance.formatInstances = (item.format_instances || []).map(formatItem => {
          const formatInstance = formatItem.format_instance || formatItem

          // Map billable instances
          formatInstance.billableInstances = (formatItem.billable_instances || []).map(billableItem => ({
            ...billableItem,
            price: billableItem.price !== undefined ? billableItem.price : 0
          }))

          formatInstance.folder_node_id = formatInstance.folder_node_id
            ?? formatItem.format_instance?.folder_node_id
            ?? null

          return formatInstance
        })

        return baseInstance
      })
    },

    async saveAll() {
      // Validate the form
      if (!this.$refs.deliverablesListForm.validate()) {
        return
      }

      // If no job ID, emit event to parent to handle saving
      if (this.isCreationMode) {
        this.$emit('save-deliverables', this.getDeliverablesData())
        return
      }

      this.saving = true

      try {
        const payload = this.getDeliverablesData()

        // Call the batch save endpoint
        await JobDeliverableAPI.saveBatchDeliverable({
          job_id: this.effectiveJobId,
          deliverables: payload
        })

        // Show success message
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('changes_saved_successfully'))

        // Reload data after saving
        await this.loadBaseInstances()

        // Emit success event
        this.$emit('deliverables-saved')
      } catch (error) {
        console.error('Error saving changes:', error)
        // Show error message to user
        this[AppActions.OPEN_APP_ERROR_MESSAGE](
          error.response?.data?.message || this.$t('error_saving_changes')
        )
      } finally {
        this.saving = false
      }
    },

    // Method to save deliverables with a job ID (called from parent after job is created)
    async saveDeliverablesForJob(jobId) {
      if (!jobId) return

      this.saving = true

      try {
        const payload = this.getDeliverablesData()

        // Call the batch save endpoint
        await JobDeliverableAPI.saveBatchDeliverable({
          job_id: jobId,
          deliverables: payload
        })

        return true
      } catch (error) {
        console.error('Error saving deliverables for job:', error)
        throw error
      } finally {
        this.saving = false
      }
    },

    // Preset loading methods
    async openLoadBaseGroupPresetsDialog() {
      this.loadingGroups = true
      this.loadBaseGroupPresetsDialog = true

      try {
        const response = await PresetAPI.getBaseGroupsByCustomer(this.effectiveCustomerId, {
          order: 'base_group_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.baseGroupOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading base groups:', error)
      } finally {
        this.loadingGroups = false
      }
    },

    async openLoadFormatGroupPresetsDialog(baseInstance) {
      this.targetBaseInstanceForFormat = baseInstance
      this.loadingGroups = true
      this.loadFormatGroupPresetsDialog = true

      try {
        const response = await PresetAPI.getFormatGroupsByCustomer(this.effectiveCustomerId, {
          order: 'format_group_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.formatGroupOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading format groups:', error)
      } finally {
        this.loadingGroups = false
      }
    },

    async loadBasePresetsForGroup() {
      if (!this.selectedBaseGroup) return

      this.loadingPresets = true
      this.basePresetOptions = []

      try {
        const response = await PresetAPI.getBasePresetsByGroup(this.selectedBaseGroup, {
          order: 'base_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.basePresetOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading base presets:', error)
      } finally {
        this.loadingPresets = false
      }
    },

    async loadFormatPresetsForGroup() {
      if (!this.selectedFormatGroup) return

      this.loadingPresets = true
      this.formatPresetOptions = []

      try {
        const response = await PresetAPI.getFormatPresetsByGroup(this.selectedFormatGroup, {
          order: 'format_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.formatPresetOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading format presets:', error)
      } finally {
        this.loadingPresets = false
      }
    },

    // Show confirmation dialog for base group
    async showBaseGroupConfirmation() {
      if (!this.selectedBaseGroup) return

      // Find the selected group
      const group = this.baseGroupOptions.find(g => g.base_group_id === this.selectedBaseGroup)
      if (!group) return

      this.selectedGroupToLoad = group
      this.confirmLoadBasePresetDialog = true
    },

    // Show confirmation dialog for format group
    async showFormatGroupConfirmation() {
      if (!this.selectedFormatGroup) return

      // Find the selected group
      const group = this.formatGroupOptions.find(g => g.format_group_id === this.selectedFormatGroup)
      if (!group) return

      this.selectedGroupToLoad = group
      this.confirmLoadFormatPresetDialog = true
    },

    // Confirm and load entire base group
    async confirmLoadBaseGroup() {
      if (!this.selectedGroupToLoad) return

      this.saving = true
      try {
        // Get all presets from the group
        const response = await PresetAPI.getBasePresetsByGroup(this.selectedGroupToLoad.base_group_id, {
          order: 'base_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        const basePresets = response.data.items || []

        // Load each preset in the group
        for (const basePreset of basePresets) {
          await this.loadBasePreset(basePreset.base_preset_id)
        }

        this.confirmLoadBasePresetDialog = false
        this.loadBaseGroupPresetsDialog = false
        this.selectedGroupToLoad = null
        this.selectedBaseGroup = null

        // Emit event to notify parent component
        this.$emit('deliverables-changed', this.getDeliverablesData())
      } catch (error) {
        console.error('Error loading base group:', error)
      } finally {
        this.saving = false
      }
    },

    // Confirm and load entire format group
    async confirmLoadFormatGroup() {
      if (!this.selectedGroupToLoad || !this.targetBaseInstanceForFormat) return

      this.saving = true
      try {
        // Get all presets from the group
        const response = await PresetAPI.getFormatPresetsByGroup(this.selectedGroupToLoad.format_group_id, {
          order: 'format_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        const formatPresets = response.data.items || []

        // Load each preset in the group
        for (const formatPreset of formatPresets) {
          await this.loadFormatPreset(formatPreset.format_preset_id)
        }

        this.confirmLoadFormatPresetDialog = false
        this.loadFormatGroupPresetsDialog = false
        this.selectedGroupToLoad = null
        this.selectedFormatGroup = null

        // Emit event to notify parent component
        this.$emit('deliverables-changed', this.getDeliverablesData())
      } catch (error) {
        console.error('Error loading format group:', error)
      } finally {
        this.saving = false
      }
    },

    async loadBasePreset(basePresetId) {
      try {
        // Get preset details with nested format and billable presets
        const presetResponse = await PresetAPI.getBasePreset(basePresetId)
        const basePreset = presetResponse.data

        // Get format presets for this base preset
        const formatPresetsResponse = await PresetAPI.getFormatPresetsByBase(basePresetId, {
          offset: 0,
          limit: 1000
        })
        const formatPresets = formatPresetsResponse.data.items || []

        // Create base instance from preset
        const newBaseInstance = {
          base_instance_id: this.tempBaseInstanceId--,
          service_id: this.effectiveServiceId,
          deliverable_id: basePreset.deliverable_id,
          rate_card_id: basePreset.rate_card_id,
          base_title: basePreset.base_title,
          base_spec: basePreset.base_spec || '',
          status_id: basePreset.status_id || this.statusOptions.find(s => s.status_type === 'waiting')?.status_id || null,
          created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
          formatInstances: []
        }

        // Create format instances from format presets
        for (const formatPreset of formatPresets) {
          // Get billable presets for this format preset
          const billablePresetsResponse = await PresetAPI.getBillablePresetsByFormat(formatPreset.format_preset_id, {
            offset: 0,
            limit: 1000
          })
          const billablePresets = billablePresetsResponse.data.items || []

          const newFormatInstance = {
            format_instance_id: this.tempFormatInstanceId--,
            base_instance_id: newBaseInstance.base_instance_id,
            status_id: formatPreset.status_id,
            format_desc: formatPreset.format_desc || '',
            format_title: formatPreset.format_title,
            quantity: formatPreset.quantity,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
            billableInstances: [],
            folder_node_id: formatPreset.folder_node_id || null
          }

          // Create billable instances from billable presets
          for (const billablePreset of billablePresets) {
            const newBillableInstance = {
              billable_instance_id: this.tempBillableInstanceId--,
              format_instance_id: newFormatInstance.format_instance_id,
              billable_id: billablePreset.billable_id,
              billable_title: billablePreset.billable_title,
              quantity: billablePreset.quantity,
              unit_label: billablePreset.unit_label,
              price: billablePreset.price,
              created_by_user: this.$store.state.core.auth.userData?.users_id || 1
            }

            newFormatInstance.billableInstances.push(newBillableInstance)
          }

          newBaseInstance.formatInstances.push(newFormatInstance)
        }

        // Add to state
        this.baseInstances.push(newBaseInstance)

        // Auto-expand the new instance
        this.$set(this.expandedDeliverables, newBaseInstance.base_instance_id, true)
      } catch (error) {
        console.error('Error loading base preset:', error)
      }
    },

    async loadFormatPreset(formatPresetId) {
      if (!this.targetBaseInstanceForFormat) return

      try {
        // Get format preset details
        const formatPresetResponse = await PresetAPI.getFormatPreset(formatPresetId)
        const formatPreset = formatPresetResponse.data

        // Get billable presets for this format preset
        const billablePresetsResponse = await PresetAPI.getBillablePresetsByFormat(formatPresetId, {
          offset: 0,
          limit: 1000
        })
        const billablePresets = billablePresetsResponse.data.items || []

        // Create format instance from preset
        const newFormatInstance = {
          format_instance_id: this.tempFormatInstanceId--,
          base_instance_id: this.targetBaseInstanceForFormat.base_instance_id,
          status_id: formatPreset.status_id,
          format_desc: formatPreset.format_desc || '',
          format_title: formatPreset.format_title,
          quantity: formatPreset.quantity,
          created_by_user: this.$store.state.core.auth.userData?.users_id || 1,
          billableInstances: []
        }

        // Create billable instances from billable presets
        for (const billablePreset of billablePresets) {
          const newBillableInstance = {
            billable_instance_id: this.tempBillableInstanceId--,
            format_instance_id: newFormatInstance.format_instance_id,
            billable_id: billablePreset.billable_id,
            billable_title: billablePreset.billable_title,
            quantity: billablePreset.quantity,
            unit_label: billablePreset.unit_label,
            price: billablePreset.price,
            created_by_user: this.$store.state.core.auth.userData?.users_id || 1
          }

          newFormatInstance.billableInstances.push(newBillableInstance)
        }

        // Ensure formatInstances array exists
        if (!this.targetBaseInstanceForFormat.formatInstances) {
          this.$set(this.targetBaseInstanceForFormat, 'formatInstances', [])
        }

        // Add to state
        this.targetBaseInstanceForFormat.formatInstances.push(newFormatInstance)

        // Auto-expand the new format
        this.$set(this.expandedFormats, newFormatInstance.format_instance_id, true)
      } catch (error) {
        console.error('Error loading format preset:', error)
      }
    },

    handleAssetUploaded() {
      // Reload deliverables when an asset is uploaded
      if (!this.isCreationMode) {
        this.loadBaseInstances()
      }
    },

    closeScreen() {
      this.$router.back()
    },

    openDeliverableAssetsView() {
      if (this.embedded) {
        this.$emit('open-deliverable-assets')
        return
      }

      this.$router.push({
        name: 'JobDeliverableAssets',
        params: {
          appId: this.$route.params.appId,
          jobId: this.effectiveJobId,
          contentAppId: this.$route.params.contentAppId,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    openDeliverableAssetsListView() {
      if (this.embedded) {
        this.$emit('open-deliverable-assets-list')
        return
      }

      this.$router.push({
        name: 'JobDeliverableAssetsList',
        params: {
          appId: this.$route.params.appId,
          jobId: this.effectiveJobId,
          contentAppId: this.$route.params.contentAppId,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    openMultiFileUploadView() {
      this.$router.push({
        name: 'JobMultiFileUpload',
        params: {
          appId: this.$route.params.appId,
          jobId: this.effectiveJobId,
          contentAppId: this.$route.params.contentAppId,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ])
  }
}
</script>

<style scoped>
.deliverable-management {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.breadcrumb-header {
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 4;
}

.card-header--embedded {
  flex-wrap: wrap;
  gap: 8px;
}

.card-header--embedded .flex-grow-1 {
  min-width: 180px;
}

.embedded-header-action {
  min-width: 0 !important;
  margin: 0 !important;
}

@media (max-width: 1200px) {
  .card-header--embedded .flex-grow-1 {
    flex-basis: 100%;
  }

  .card-header--embedded .deliverable-toggle-all {
    margin-left: 0 !important;
  }

  .card-header--embedded .embedded-header-action-label {
    display: none;
  }

  .card-header--embedded .embedded-header-action {
    min-width: 36px !important;
    padding: 0 8px !important;
  }
}

.deliverable-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f4f5f9;
}

.deliverable-header {
  transition: all 0.2s;
}

.deliverable-header-row {
  border-radius: 8px;
  background-color: #f4f5f9;
  transition: background-color 0.2s;
}

.format-item {
  background-color: #eaebef;
  border-left: 5px solid var(--v-primary-base);
  border-radius: 10px;
}

.format-card {
  border-radius: 4px;
}

.billables-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.billables-table >>> thead th {
  background-color: #f5f5f5 !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

.billables-table >>> tbody td {
  font-size: 0.875rem !important;
}

.billables-table >>> tfoot td {
  background-color: #f5f5f5 !important;
  font-size: 0.875rem !important;
}

.centered-input >>> input {
  text-align: center !important;
}

.billables-table >>> tbody td .v-text-field {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.billables-table >>> tbody td .v-input__control {
  min-height: 32px !important;
}

.billables-table >>> tbody td .v-input__slot {
  margin-bottom: 0 !important;
  min-height: 32px !important;
}

.custom-vertical-divider {
  border-width: 0 medium 0 0 !important;
  border-color: var(--v-primary-base);
}
</style>
